import React, { useEffect } from 'react';
import { X } from 'lucide-react';

export const NewsModal = ({ article, onClose }) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  if (!article) return null;

  // Generate more content for the article detail
  const fullContent = `${article.excerpt}

This is developing news that has captured attention worldwide. Experts and analysts have been closely monitoring the situation, offering various perspectives on its implications.

According to sources close to the matter, this development represents a significant shift in the landscape. Industry observers note that the ramifications could be far-reaching, affecting multiple sectors and stakeholders.

"This is a pivotal moment," said one analyst who spoke on condition of anonymity. "The decisions made now will shape the trajectory for years to come."

The news has sparked discussions across social media platforms, with many users sharing their thoughts and reactions. Government officials have also weighed in, emphasizing the importance of staying informed and engaged.

As the story continues to unfold, our team will provide updates and analysis. Stay tuned for more comprehensive coverage of this significant development.`;

  return (
    <div 
      className="modal-overlay animate-fadeIn"
      onClick={onClose}
      data-testid="news-modal-overlay"
    >
      <div 
        className="modal-content animate-slideUp"
        onClick={(e) => e.stopPropagation()}
        data-testid="news-modal-content"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white hover:bg-gray-100 transition-colors"
          data-testid="modal-close-btn"
          aria-label="Close modal"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Image */}
        <div className="aspect-[16/9] w-full">
          <img 
            src={article.image_url} 
            alt={article.title}
            className="w-full h-full object-cover"
            data-testid="modal-image"
          />
        </div>

        {/* Content */}
        <div className="p-6 md:p-10">
          {/* Category */}
          <span 
            className="category-badge block mb-3"
            data-testid="modal-category"
          >
            {article.category}
          </span>

          {/* Title */}
          <h1 
            className="font-heading font-black text-3xl md:text-4xl lg:text-5xl leading-tight mb-4"
            data-testid="modal-title"
          >
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center gap-4 mb-6 text-sm text-gray-500 font-mono">
            <time data-testid="modal-date">{article.date}</time>
            <span>|</span>
            <span>5 min read</span>
          </div>

          {/* Article Content */}
          <div 
            className="prose prose-lg max-w-none font-body text-gray-800 leading-relaxed"
            data-testid="modal-content"
          >
            {fullContent.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-4">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Share Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm font-secondary font-semibold text-gray-700 mb-3">Share this article</p>
            <div className="flex gap-3">
              <button 
                className="filter-btn"
                data-testid="share-twitter"
              >
                Twitter
              </button>
              <button 
                className="filter-btn"
                data-testid="share-facebook"
              >
                Facebook
              </button>
              <button 
                className="filter-btn"
                data-testid="share-linkedin"
              >
                LinkedIn
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsModal;
